import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Building2, Mail, Phone, MapPin, Upload, FileText, CheckCircle2,
  ArrowRight, Clock, Sparkles, ShieldCheck, TrendingDown, Leaf, X,
} from "lucide-react";
import { Logo } from "@/components/Logo";
import { ProcessingScreen } from "@/components/ProcessingScreen";
import { api, SETTORI, PROVINCE, waitForStatus } from "@/lib/api";
import COMUNI from "@/lib/comuni.json";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const HERO_IMG = "https://images.pexels.com/photos/4254157/pexels-photo-4254157.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=1600";

const PROC_STEPS = [
  "Lettura documento e riconoscimento layout…",
  "Estrazione POD / PDR e dati anagrafici…",
  "Analisi consumi, potenza e fasce F1/F2/F3…",
  "Calcolo prezzo medio €/kWh e componenti di costo…",
  "Confronto con benchmark di settore…",
  "Individuazione aree di miglioramento…",
  "Composizione Preliminary Energy Score…",
];

const MultiFileField = ({ id, label, hint, files, onChange, required }) => {
  const addFiles = (list) => onChange([...files, ...Array.from(list || [])]);
  const remove = (i) => onChange(files.filter((_, idx) => idx !== i));
  return (
    <div>
      <Label className="text-slate-300 text-sm mb-2 block">
        {label} {required && <span className="text-gold-400">*</span>}
        {!required && <span className="text-slate-500 text-xs ml-1">(consigliate)</span>}
      </Label>

      {files.length > 0 && (
        <div className="space-y-2 mb-2.5">
          {files.map((f, i) => (
            <div key={i} data-testid={`${id}-file-${i}`}
              className="flex items-center gap-2.5 rounded-lg border border-gold-500/40 bg-gold-500/5 px-3 py-2">
              <CheckCircle2 className="h-4 w-4 text-gold-400 shrink-0" />
              <span className="text-sm text-white truncate flex-1">{f.name}</span>
              <button type="button" data-testid={`${id}-remove-${i}`} onClick={() => remove(i)}
                className="text-slate-400 hover:text-red-400 transition-colors shrink-0">
                <X className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      <label htmlFor={id} data-testid={`${id}-dropzone`}
        className="flex items-center gap-3 rounded-xl border border-dashed border-white/15 hover:border-gold-500/40 bg-white/[0.02] px-4 py-3.5 cursor-pointer transition-colors">
        <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-white/5">
          <Upload className="h-4.5 w-4.5 text-slate-400" />
        </div>
        <div className="min-w-0">
          <p className="text-sm text-white">{files.length ? "Aggiungi un'altra fattura" : "Carica file"}</p>
          <p className="text-xs text-slate-500">{hint}</p>
        </div>
      </label>
      <input id={id} data-testid={`${id}-input`} type="file" multiple accept="application/pdf,image/*,.pdf,.jpg,.jpeg,.png,.webp,.heic,.heif" className="hidden"
        onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} />
    </div>
  );
};

export default function Landing() {
  const navigate = useNavigate();
  const [processing, setProcessing] = useState(false);
  const [billProgress, setBillProgress] = useState(null);
  const [realPct, setRealPct] = useState(null);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({
    ragione_sociale: "", email: "", telefono: "", settore: "", provincia: "", citta: "", settore_altro: "",
  });
  const [gdprConsent, setGdprConsent] = useState(false);
  const [energyFiles, setEnergyFiles] = useState([]);
  const [gasFiles, setGasFiles] = useState([]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.ragione_sociale || !form.email || !form.telefono || !form.settore || !form.provincia || !form.citta) {
      toast.error("Compila tutti i campi obbligatori");
      return;
    }
    if (form.settore === "Altro" && !form.settore_altro.trim()) {
      toast.error("Specifica il tuo settore di attività");
      return;
    }
    if (energyFiles.length === 0 && gasFiles.length === 0) { toast.error("Carica almeno una bolletta: energia elettrica o gas"); return; }
    if (!gdprConsent) { toast.error("Per procedere devi acconsentire al trattamento dei dati personali (GDPR)"); return; }

    setProcessing(true);
    setDone(false);
    setRealPct(null);
    setBillProgress(null);
    try {
      const uploadId = (window.crypto && window.crypto.randomUUID)
        ? window.crypto.randomUUID()
        : `up-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      const effSettore = form.settore === "Altro" ? form.settore_altro.trim() : form.settore;
      let src = null;
      try { src = new URLSearchParams(window.location.search).get("src"); } catch (e) {}

      // 1) Carico le bollette UNA ALLA VOLTA (richieste leggere) per evitare il
      //    504 del gateway sugli upload cumulativi grandi.
      const total = energyFiles.length + gasFiles.length;
      let uploaded = 0;
      const uploadOne = async (file, box, seq) => {
        const ufd = new FormData();
        ufd.append("upload_id", uploadId);
        ufd.append("box", box);
        ufd.append("seq", String(seq));
        ufd.append("file", file);
        await api.post("/leads/upload-file", ufd, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded += 1;
        setBillProgress(`Caricamento bolletta ${uploaded} di ${total}…`);
        setRealPct(Math.round((uploaded / total) * 25));
      };
      setBillProgress(`Caricamento bolletta 1 di ${total}…`);
      for (let i = 0; i < energyFiles.length; i++) await uploadOne(energyFiles[i], "energy", i);
      for (let i = 0; i < gasFiles.length; i++) await uploadOne(gasFiles[i], "gas", i);

      // 2) Finalizzo: avvia l'analisi (stessa pipeline di prima).
      const ffd = new FormData();
      ffd.append("upload_id", uploadId);
      Object.entries(form).forEach(([k, v]) => {
        if (k === "settore" || k === "settore_altro") return;
        ffd.append(k, v);
      });
      ffd.append("settore", effSettore);
      ffd.append("gdpr_consent", "true");
      if (src) ffd.append("provenienza", src);
      const { data } = await api.post("/leads/finalize", ffd, { headers: { "Content-Type": "multipart/form-data" } });
      const leadId = data.lead_id;
      setBillProgress("Analisi in corso…");
      const final = await waitForStatus(leadId, ["preliminary", "completed", "error"], {
        onPoll: (data) => {
          const p = data?.phase1_progress;
          if (p && p.total > 0) {
            setBillProgress(`Documento ${Math.min(p.done + 1, p.total)} di ${p.total} in analisi…`);
            setRealPct(25 + Math.round((p.done / p.total) * 63));
          }
        },
      });
      if (final === "error" || final === "timeout") {
        setProcessing(false);
        toast.error("Errore durante l'analisi delle bollette. Riprova.");
        return;
      }
      setDone(true);
      setTimeout(() => navigate(`/dashboard/${leadId}`), 1400);
    } catch (err) {
      setProcessing(false);
      toast.error("Errore durante l'analisi. Riprova.");
    }
  };

  if (processing) {
    return <ProcessingScreen steps={PROC_STEPS} done={done} progressLabel={billProgress} realProgress={realPct}
      title="La nostra AI sta analizzando le tue bollette"
      subtitle="Estrazione automatica dei dati e prima valutazione. Attendi qualche istante." />;
  }

  return (
    <div className="min-h-screen relative">
      {/* Nav */}
      <header className="fixed top-0 inset-x-0 z-30 glass">
        <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
          <Logo height={60} />
          <Link to="/admin/login" data-testid="nav-admin-link"
            className="text-sm text-slate-400 hover:text-gold-400 transition-colors">
            Area riservata
          </Link>
        </div>
      </header>

      {/* Hero + Form */}
      <section className="relative pt-20 min-h-screen grid lg:grid-cols-2">
        {/* Left */}
        <div className="relative flex items-center px-6 sm:px-12 lg:px-16 py-16">
          <div className="absolute inset-0 grid-lines opacity-30" />
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            className="relative max-w-xl">
            <div className="inline-flex items-center gap-2 rounded-full border border-gold-500/30 bg-gold-500/5 px-3.5 py-1.5 text-xs text-gold-300 mb-7">
              <Clock className="h-3.5 w-3.5" /> Check-up gratuito · meno di 2 minuti
            </div>
            <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.02] text-white">
              La tua azienda paga <span className="text-gold-400 text-glow">più del necessario</span> per energia e gas?
            </h1>
            <p className="mt-6 text-slate-300 text-base leading-relaxed">
              Carica le tue ultime bollette e ricevi una prima valutazione gratuita con le
              opportunità di risparmio individuate dalla nostra <span className="text-white font-medium">Intelligenza Artificiale</span>.
            </p>

            <div className="mt-9 grid grid-cols-3 gap-3">
              {[
                { icon: Sparkles, t: "Analisi AI", s: "Lettura automatica bollette" },
                { icon: TrendingDown, t: "Risparmio", s: "Potenziale annuo stimato" },
                { icon: Leaf, t: "CO₂", s: "Riduzione emissioni" },
              ].map((b, i) => (
                <div key={i} className="card-dark rounded-xl p-3.5">
                  <b.icon className="h-4.5 w-4.5 text-gold-400 mb-2" />
                  <p className="text-white text-sm font-medium">{b.t}</p>
                  <p className="text-slate-500 text-[11px] leading-tight mt-0.5">{b.s}</p>
                </div>
              ))}
            </div>

            <div className="mt-8 flex items-center gap-2 text-xs text-slate-500">
              <ShieldCheck className="h-4 w-4 text-gold-400/70" />
              Dati trattati in modo riservato · nessun impegno
            </div>

            <div className="mt-10 hidden lg:block rounded-2xl overflow-hidden border border-white/10 relative h-44">
              <img src={HERO_IMG} alt="Consulenza fotovoltaica DM Solutions Group" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A1A2F] via-[#0A1A2F]/30 to-transparent" />
            </div>
          </motion.div>
        </div>

        {/* Right form */}
        <div className="relative flex items-center px-6 sm:px-12 lg:px-16 py-16 bg-[#0C2038] border-l border-white/5">
          <motion.form
            onSubmit={submit}
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }}
            className="w-full max-w-md card-dark rounded-2xl p-7 glow-gold"
            data-testid="checkup-form"
          >
            <h2 className="font-heading font-bold text-xl text-white mb-1">Check-up energetico gratuito</h2>
            <p className="text-slate-500 text-sm mb-6">Ci servono solo pochi dati.</p>

            <div className="space-y-4">
              <Field icon={Building2} label="Ragione sociale" required>
                <Input data-testid="input-ragione-sociale" value={form.ragione_sociale} onChange={set("ragione_sociale")}
                  placeholder="Es. Rossi Manifatture S.r.l." className="input-dark" />
              </Field>
              <div className="grid grid-cols-2 gap-4">
                <Field icon={Mail} label="Email" required>
                  <Input data-testid="input-email" type="email" value={form.email} onChange={set("email")}
                    placeholder="nome@azienda.it" className="input-dark" />
                </Field>
                <Field icon={Phone} label="Telefono" required>
                  <Input data-testid="input-telefono" value={form.telefono} onChange={set("telefono")}
                    placeholder="+39 …" className="input-dark" />
                </Field>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-300 text-sm mb-2 block">Settore <span className="text-gold-400">*</span></Label>
                  <Select value={form.settore} onValueChange={set("settore")}>
                    <SelectTrigger data-testid="select-settore" className="input-dark"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                    <SelectContent className="bg-[#0F2440] border-white/10 text-white max-h-64">
                      {SETTORI.map((s) => <SelectItem key={s} value={s} className="focus:bg-gold-500/15">{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-slate-300 text-sm mb-2 block">Provincia <span className="text-gold-400">*</span></Label>
                  <Select value={form.provincia} onValueChange={(v) => setForm((f) => ({ ...f, provincia: v, citta: "" }))}>
                    <SelectTrigger data-testid="select-provincia" className="input-dark"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                    <SelectContent className="bg-[#0F2440] border-white/10 text-white max-h-64">
                      {PROVINCE.map((p) => <SelectItem key={p} value={p} className="focus:bg-gold-500/15">{p}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="text-slate-300 text-sm mb-2 block">Città / Comune <span className="text-gold-400">*</span></Label>
                <Input
                  data-testid="input-citta"
                  list="comuni-list"
                  value={form.citta}
                  onChange={set("citta")}
                  disabled={!form.provincia}
                  placeholder={form.provincia ? "Inizia a digitare il tuo comune…" : "Seleziona prima la provincia"}
                  className="input-dark disabled:opacity-50 disabled:cursor-not-allowed"
                  autoComplete="off"
                />
                <datalist id="comuni-list">
                  {(COMUNI[form.provincia] || []).map((c) => <option key={c} value={c} />)}
                </datalist>
              </div>

              {form.settore === "Altro" && (
                <Field icon={Building2} label="Specifica il tuo settore" required>
                  <Input data-testid="input-settore-altro" value={form.settore_altro} onChange={set("settore_altro")}
                    placeholder="Es. Vetreria artistica, Cartotecnica…" className="input-dark" />
                </Field>
              )}

              <MultiFileField id="energy-bill" label="Bollette energia elettrica"
                hint="Solo PDF o foto/scansione · più contatori? Aggiungi più file"
                files={energyFiles} onChange={setEnergyFiles} />
              <MultiFileField id="gas-bill" label="Bollette gas"
                hint="Solo PDF o foto/scansione (una per ogni PDR)"
                files={gasFiles} onChange={setGasFiles} />
              <p className="text-xs text-slate-500 -mt-1" data-testid="bills-hint">Carica almeno una bolletta: energia elettrica <span className="text-slate-400">oppure</span> gas.</p>
            </div>

            <label htmlFor="gdpr-consent" data-testid="gdpr-consent-label"
              className="mt-5 flex items-start gap-2.5 rounded-xl border border-white/10 bg-white/[0.02] px-3.5 py-3 cursor-pointer hover:border-gold-500/30 transition-colors">
              <input id="gdpr-consent" type="checkbox" data-testid="gdpr-consent-checkbox"
                checked={gdprConsent} onChange={(e) => setGdprConsent(e.target.checked)}
                className="mt-0.5 h-4 w-4 shrink-0 accent-gold-500 cursor-pointer" />
              <span className="text-[11px] leading-relaxed text-slate-400">
                Acconsento al <span className="text-slate-300">trattamento dei dati personali</span> (Reg. UE 2016/679 - GDPR).
                Autorizzo DM Solutions Group SRLS a contattarmi e a inviarmi offerte, preventivi e comunicazioni
                relative ai servizi, ora e in futuro, per tutto quanto necessario. Potrò richiedere in qualsiasi
                momento la rimozione dai sistemi e la cessazione di ogni comunicazione inviando una PEC a
                <span className="text-gold-400"> info@dmsolutionsgroup.it</span>. Consulta l'
                <Link to="/privacy" target="_blank" onClick={(e) => e.stopPropagation()}
                  className="text-gold-400 underline hover:text-gold-300" data-testid="gdpr-privacy-link">Informativa Privacy</Link>. <span className="text-gold-400">*</span>
              </span>
            </label>

            <Button type="submit" data-testid="submit-checkup-btn"
              className="w-full mt-6 h-12 rounded-xl bg-gold-500 hover:bg-gold-400 text-[#0A1A2F] font-semibold text-base group">
              Avvia il check-up gratuito
              <ArrowRight className="h-4.5 w-4.5 ml-1.5 group-hover:translate-x-1 transition-transform" />
            </Button>
            <p className="text-center text-[11px] text-slate-600 mt-3">
              <FileText className="h-3 w-3 inline mr-1" /> Nessun report generico. Ricevi una dashboard con il tuo Energy Score.
            </p>
          </motion.form>
        </div>
      </section>

      <footer className="border-t border-white/10 py-8 px-6" data-testid="landing-footer">
        <div className="max-w-3xl mx-auto text-center space-y-3">
          <p className="text-[12px] text-slate-500 leading-relaxed" data-testid="consulenza-clause">
            Per la gestione delle forniture di energia e gas, oltre alle successive valutazioni e modifiche di prodotto,
            all'audit predittivo e alle ulteriori soluzioni offerte, è necessario sottoscrivere il contratto di consulenza con la nostra società.
          </p>
          <p className="text-[11px] text-slate-600">
            DM Solutions Group SRLS · Via Panaro 85, Agliana (PT) · P.IVA/C.F. 02132120474 · info@dmsolutionsgroup.it
          </p>
        </div>
      </footer>
    </div>
  );
}

const Field = ({ icon: Icon, label, required, children }) => (
  <div>
    <Label className="text-slate-300 text-sm mb-2 flex items-center gap-1.5">
      <Icon className="h-3.5 w-3.5 text-slate-500" /> {label} {required && <span className="text-gold-400">*</span>}
    </Label>
    {children}
  </div>
);
